import { useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';

import { CurrencyIcon } from '@/entities/currency';
import { addToFavMutation } from '@/entities/payment';
import { IPaymentTemplate } from '@/entities/payment/lib/serialize';
import { usePaymentsController } from '@/entities/payment/model';

import { VabsParticipantType, VabsPaymentSystem } from '@/shared/api';
import { DashboardTrashIcon, Human, LogoIconSmall, MinusIcon, PlusIcon } from '@/shared/assets';
import { shortText } from '@/shared/lib/utils/text';
import { AvatarWithIndicator, EmptyState, Label, ModalTemplate, SearchInput, Skeleton, Typography } from '@/shared/ui';

const getPaymentName = (payment: IPaymentTemplate) => {
  if (payment.paymentSystem === VabsPaymentSystem.Inner) return payment.name || '';
  if (payment.type === VabsParticipantType.B2C) {
    return `${payment.firstName || ''} ${payment.lastName || ''}`.trim();
  }
  return payment.companyName || '';
};

const getPaymentSubline = (payment: IPaymentTemplate) => {
  if (payment.paymentSystem === VabsPaymentSystem.Inner) return 'FCB account';
  return `${payment.currency?.ticker || ''} account: ${shortText(payment.accountNumber || '', 6)}`;
};

const PaymentFavoriteRow = ({
  payment,
  isSelected,
  onToggle,
  isPending,
}: {
  payment: IPaymentTemplate;
  isSelected: boolean;
  onToggle: (id: string, isFavorite: boolean) => void;
  isPending?: boolean;
}) => {
  const isInner = payment.paymentSystem === VabsPaymentSystem.Inner;

  return (
    <li className="flex h-[72px] items-center justify-between rounded-[20px] px-2">
      <div className="flex min-w-0 items-center gap-3">
        <AvatarWithIndicator
          defaultAvatar={
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-base-300">
              <Human className="!h-6 !w-6" />
            </div>
          }
          indicatorClassName="bg-base-500"
          indicator={
            isInner ? (
              <LogoIconSmall className="bg-base-500 [&_svg]:!h-5 [&_svg]:!w-5" />
            ) : (
              <CurrencyIcon
                ticker={payment.currency?.ticker}
                className="[&_svg]:!h-6 [&_svg]:!w-6"
              />
            )
          }
        />

        <div className="flex min-w-0 flex-col">
          <Typography className="truncate">{shortText(getPaymentName(payment), 24)}</Typography>
          <Typography variant="14-r" color="text-base-100" className="truncate">
            {getPaymentSubline(payment)}
          </Typography>
        </div>
      </div>

      <button
        type="button"
        disabled={isPending}
        onClick={() => {
          onToggle(payment.id, !isSelected);
        }}
        className={twMerge(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          isSelected ? 'bg-contrast-500 text-contrast-500' : 'bg-base-300 text-base-500',
          isPending && 'cursor-not-allowed opacity-60',
        )}
      >
        {isSelected ? <MinusIcon className="w-5" /> : <PlusIcon className="w-5" />}
      </button>
    </li>
  );
};

const ListSkeleton = () => (
  <div className="flex flex-col gap-4 px-2 py-2">
    {Array.from({ length: 4 }).map((_, idx) => (
      <div key={idx} className="flex w-full items-center gap-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-5 w-[45%] rounded-xl" />
          <Skeleton className="mt-1 h-4 w-[60%] rounded-xl" />
        </div>
      </div>
    ))}
  </div>
);

const Content = () => {
  const [search, setSearch] = useState('');
  const [pendingId, setPendingId] = useState('');
  const [optimisticFavoriteState, setOptimisticFavoriteState] = useState<
    Record<string, boolean>
  >({});
  const { payments, favorites, isLoading, isFavoritesLoading, onSearch, onFavSearch } =
    usePaymentsController(true) as {
      payments: IPaymentTemplate[];
      favorites: IPaymentTemplate[];
      isLoading: boolean;
      isFavoritesLoading: boolean;
      onSearch: (value: string) => void;
      onFavSearch: (value: string) => void;
    };
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: addToFavMutation,
  });

  const favoriteIds = useMemo(() => {
    return new Set((favorites || []).map((item) => item.id));
  }, [favorites]);

  const paymentsById = useMemo(() => {
    const map = new Map<string, IPaymentTemplate>();
    (favorites || []).forEach((payment) => {
      map.set(payment.id, payment);
    });
    (payments || []).forEach((payment) => {
      if (!map.has(payment.id)) map.set(payment.id, payment);
    });
    return map;
  }, [favorites, payments]);

  const effectiveFavoriteIds = useMemo(() => {
    const ids = new Set(favoriteIds);
    Object.entries(optimisticFavoriteState).forEach(([paymentId, isFavorite]) => {
      if (isFavorite) ids.add(paymentId);
      else ids.delete(paymentId);
    });
    return ids;
  }, [favoriteIds, optimisticFavoriteState]);

  useEffect(() => {
    setOptimisticFavoriteState((prev) => {
      const nextEntries = Object.entries(prev).filter(
        ([paymentId, isFavorite]) => favoriteIds.has(paymentId) !== isFavorite,
      );

      if (nextEntries.length === Object.keys(prev).length) return prev;

      return Object.fromEntries(nextEntries);
    });
  }, [favoriteIds]);

  const favoritePayments = useMemo(
    () =>
      Array.from(paymentsById.values()).filter((payment) =>
        effectiveFavoriteIds.has(payment.id),
      ),
    [paymentsById, effectiveFavoriteIds],
  );

  const lastOperations = useMemo(
    () =>
      Array.from(paymentsById.values()).filter(
        (payment) => !effectiveFavoriteIds.has(payment.id),
      ),
    [paymentsById, effectiveFavoriteIds],
  );

  const onToggleFavorite = async (paymentId: string, isFavorite: boolean) => {
    const previousState = effectiveFavoriteIds.has(paymentId);
    setOptimisticFavoriteState((prev) => ({
      ...prev,
      [paymentId]: isFavorite,
    }));

    try {
      setPendingId(paymentId);
      await mutateAsync({ paymentId, isFavorite });
      void queryClient.invalidateQueries({ queryKey: ['txs-list'] });
      void queryClient.invalidateQueries({ queryKey: ['payments-list'] });
      void queryClient.invalidateQueries({ queryKey: ['faves-list'] });
      if (isFavorite) {
        toast.success('Added to favourites')
      } else {
        toast.custom(() => <div className="flex items-center gap-2">
          <DashboardTrashIcon className="!size-4" />
          <Typography
            variant="14-r">Removed from favorites</Typography>
        </div>)
      }
    } catch (error) {
      setOptimisticFavoriteState((prev) => ({
        ...prev,
        [paymentId]: previousState,
      }));
      console.log(error);
      toast.error('Failed to update favourites');
    } finally {
      setPendingId('');
    }
  };

  return (
    <div className="flex w-full flex-1 flex-col overflow-hidden px-3 pb-5">
      <div className="pb-5 pt-1">
        <Label className="relative flex h-fit w-full flex-col">
          <SearchInput
            value={search}
            onSearch={(value) => {
              setSearch(value);
              onSearch(value);
              onFavSearch(value);
            }}
            onClear={() => {
              setSearch('');
              onSearch('');
              onFavSearch('');
            }}
            placeholder="Search"
          />
        </Label>
      </div>

      <div className="no-scrollbar flex-1 space-y-3 overflow-y-auto pr-1">
        {isLoading || isFavoritesLoading ? (
          <ListSkeleton />
        ) : (
          <>
            {!!favoritePayments?.length && (
              <ul className="space-y-1">
                {favoritePayments.map((payment) => (
                  <PaymentFavoriteRow
                    key={payment.id}
                    payment={payment}
                    isSelected
                    isPending={pendingId === payment.id}
                    onToggle={onToggleFavorite}
                  />
                ))}
              </ul>
            )}

            <Typography variant="14-r" color="text-base-100" className="pl-2 pt-2">
              Last operations
            </Typography>

            {lastOperations.length ? (
              <ul className="space-y-1">
                {lastOperations.map((payment) => (
                  <PaymentFavoriteRow
                    key={payment.id}
                    payment={payment}
                    isSelected={effectiveFavoriteIds.has(payment.id)}
                    isPending={pendingId === payment.id}
                    onToggle={onToggleFavorite}
                  />
                ))}
              </ul>
            ) : (
              <EmptyState className="pb-5" />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const FavoritesModal = () => {
  return (
    <ModalTemplate
      id="favorites-modal"
      title="Favourite payments"
      isHasCloseBtn
      description="Save a payment to access it faster next time"
      classNames={{
        wrapper: 'rounded-[32px] px-0',
        title: 'uppercase',
        description: 'typography-14r max-w-[220px] mx-auto pt-2'
      }}
    >
      <Content />
    </ModalTemplate>
  );
};
